import {sumBy} from 'lodash';
import {HoistModel, LoadSpec, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {HoldingsTypeEnum, PerformanceCalcData, Position, TaxTreatmentEnum} from '../../../../data/DataTypes';

/**
 * Performance figures from the live positions and the server's year-start values - of all the non-cash portfolios, or
 * of one portfolio once `portfolioId` is set. Shared by the desktop Performance widget and the mobile summary.
 */
export class PerformanceWidgetModel extends HoistModel {
    /** One portfolio, or null for all the non-cash portfolios. Reload after changing it. */
    portfolioId: number = null;
    @observableRef accessor calcData: PerformanceCalcData;
    @observable accessor mv: number;
    @observable accessor dly: number;
    @observable accessor ytd: number;
    @observable accessor ytdDva: number;
    @observable accessor ytdPct: number;
    @observable accessor ytdDvaPct: number;
    @observable accessor cagr: number;
    @observable accessor beta: number;
    @observable accessor rmd: number;
    @observable accessor dividends: number;
    @observable accessor swr: number;

    constructor() {
        super();

        this.addReaction({
            track: () => XH.livePositionsService.cube.store.lastUpdated,
            run: () => this.recalc()
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const data = await XH.performanceService.fetchCalcDataAsync(this.portfolioId);
        if (loadSpec.isStale) return;
        runInAction(() => {
            this.calcData = data;
        });

        this.recalc();
    }

    @action private recalc() {
        if (!this.calcData) return;

        const allPositions = XH.livePositionsService.cube.store.allRecords.map(r => r.data as Position),
            {portfolioId} = this,
            positions = allPositions.filter(p => (portfolioId != null ? p.portfolioId === portfolioId : p.portfolioHoldingType != HoldingsTypeEnum.Cash)),
            iraPositions = positions.filter(p => p.portfolioTaxType == TaxTreatmentEnum.TaxDeferred),
            dly = sumBy(positions, 'pnlDly'),
            mv = sumBy(positions, 'marketValue'),
            iraMv = sumBy(iraPositions, 'marketValue'),
            div = sumBy(positions, 'lastDividend'),
            bMv = sumBy(positions, p => p.beta * p.marketValue);

        this.mv = mv;
        this.dly = dly;
        this.ytdDva = mv - this.calcData.endOfLastYearMarketValue;
        this.ytd = this.ytdDva - this.calcData.currentYearDeposits;
        this.ytdDvaPct = this.ytdDva / this.calcData.endOfLastYearMarketValue;
        this.ytdPct = this.ytd / this.calcData.endOfLastYearMarketValue;

        this.cagr = this.calcData.cagr;
        this.swr = mv * 0.04;
        this.rmd = iraMv * 0.04;
        this.dividends = div;
        this.beta = bMv / mv;
    }
}
